import { uniq } from 'ramda';
import { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { ProviderResultItem } from '~/clients';

import { decodeProviderIdForReactHookForm, PROVIDER_ID_LOCATION_ID_DELIMETER } from './common';

type ProvidersTableRowId = {
  providerId: string;
  locationId?: string;
};

export function useSelectedProviderIds(form: UseFormReturn): {
  selectedProviderInfo: ProvidersTableRowId[];
  isProviderResultSelected: (p: ProviderResultItem) => boolean;
} {
  const [selectedProviderInfo, setSelectedProviderInfo] = useState<ProvidersTableRowId[]>([]);
  // This whole thing is a bit of a mess. This is intended to solve for 2 cases:
  // 1. A search that returns professionals - this is easy, as provider ID is the unique identifier of any given row
  //    regardless of if its NDS or if its ranked search as the collapsing for multiple locations for a single provider
  //    happens upstream of that provider being rendered in the table
  // 2. A search that returns facilities - this is more difficult as we want to ensure that the user still only selects
  //    PROVIDER_ID_LOCATION_ID_DELIMETER rows of the table, but table rows are not uniquely identified by a provider ID.
  //    given that we need to take location ID into account and encode that information in the form of a key of a form.
  useEffect(() => {
    const { unsubscribe } = form.watch(formValues => {
      setSelectedProviderInfo(
        // JS's built in Set uses object references for uniqueness, we care about object
        uniq(
          Object.entries(formValues)
            .map(([k, v]) => {
              if (k.startsWith('checkbox-') && v) {
                const id = decodeProviderIdForReactHookForm(k.slice(9));
                if (id.startsWith('p.')) return { providerId: id };

                const [providerId, locationId] = id.split(PROVIDER_ID_LOCATION_ID_DELIMETER);
                return { providerId, locationId };
              }
              return null;
            })
            .filter(Boolean),
        ),
      );
    });
    return () => unsubscribe();
  }, [form.watch]);

  return {
    selectedProviderInfo,
    isProviderResultSelected: p =>
      selectedProviderInfo.some(selectedProvider => {
        if (p.type === 'physician') {
          return p.id === selectedProvider.providerId;
        }
        return p.id === selectedProvider.providerId && p.location.id === selectedProvider.locationId;
      }),
  };
}
